/**
 * Returns overall perceived brightness of the given color in
 * range from 0 to 1. Weights for each of RGB components are around
 * what's usually used for grayscale conversions, `0.6` for green,
 * `0.3` for red and `0.1` for blue.
 */
export function getWeightedBrightness(color: string): number {
  const rgba = parseHex(color) || parseCss(color);

  if (!rgba) {
    return 0;
  }

  const { red, green, blue, alpha } = rgba;

  // Round to get 1 instead of 0.99999...
  return Math.round(alpha * (0.3 * red + 0.6 * green + 0.1 * blue) * 1000) / 1000;
}

/**
 * RGB color representation with alpha channel
 */
export interface IRGBA {
  /**
   * Value for the red component, `0-1` (incl.)
   */
  red: number;
  /**
   * Value for the green component, `0-1` (incl.)
   */
  green: number;
  /**
   * Value for the blue component, `0-1` (incl.)
   */
  blue: number;
  /**
   * Value for the alpha component, `0-1` (incl.)
   */
  alpha: number;
}

/**
 * HSV color representation with alpha channel
 */
export interface IHSVA {
  /**
   * Hue component, `0-360` (incl.)
   */
  hue: number;
  /**
   * Saturation, `0-1` (incl.)
   */
  saturation: number;
  /**
   * Overall brightness `0-1` (incl.)
   */
  value: number;
  /**
   * Value for the alpha component, `0-1` (incl.)
   */
  alpha: number;
}

export function parseHex(color: string): IRGBA | undefined {
  const components =
    // #rgb or #rgba (with alpha)
    /^#?([0-9a-f])([0-9a-f])([0-9a-f])([0-9a-f])?$/i.exec(color) ||
    // #rrggbb or #rrggbbaa
    /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})?$/i.exec(color);

  if (!components) {
    return undefined;
  }

  return {
    red:   parseHexComponent(components[1]),
    green: parseHexComponent(components[2]),
    blue:  parseHexComponent(components[3]),
    alpha: components[4] ? parseHexComponent(components[4]) : 1,
  };
}

function parseHexComponent(hex: string): number {
  if (hex.length === 1) {
    return parseHexComponent(`${hex}${hex}`);
  }

  return Number.parseInt(hex, 16) / 255;
}

export function parseCss(color: string): IRGBA | undefined {
  const components =
    /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/.exec(color) ||
    /^rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d?\.?\d*)\s*\)$/.exec(color);

  if (!components) {
    return undefined;
  }

  return {
    red:   Number.parseInt(components[1], 10) / 255,
    green: Number.parseInt(components[2], 10) / 255,
    blue:  Number.parseInt(components[3], 10) / 255,
    alpha: components[4] ? Number.parseFloat(components[4]) : 1,
  };
}

export const toHexRgb = ({ red, green, blue }: Pick<IRGBA, 'red' | 'green' | 'blue'>) =>
  `#${twoDigitHex(red * 255)}${twoDigitHex(green * 255)}${twoDigitHex(blue * 255)}`;

export const toHexRgba = ({ alpha, ...rgb }: IRGBA) =>
  `${toHexRgb(rgb)}${twoDigitHex(alpha * 255)}`;

function twoDigitHex(n: number) {
  if (n >= 255) {
    return 'ff';
  } else if (n < 0) {
    return '00';
  } else {
    const str = Math.round(n).toString(16);
    return str.length < 2 ? `0${str}` : str;
  }
}

/**
 * Returns an HSV representation of the RGB color, based on Wikipedia
 * article on HSV
 */
export function rgbToHsv({ red, green, blue, alpha }: IRGBA): IHSVA {
  const value = Math.max(red, green, blue);
  const chroma = value - Math.min(red, green, blue);

  const hue =
    chroma === 0 ? 0 :
    60 * (
      value === red ? (green - blue) / chroma :
      value === green ? (blue - red) / chroma + 2 :
      /* value === blue */ (red - green) / chroma + 4
    );

  return {
    hue:
      hue >= 360 ? hue - 360 : // normalise 360 to 0
      hue < 0 ? hue + 360 :
      hue,
    saturation: value > 0 ? chroma / value : 0,
    value,
    alpha,
  };
}

/**
 * Returns an RGB representation of the HSV color, based on Wikipedia
 * article on HSV
 */
export function hsvToRgb({ hue, saturation, value, alpha }: IHSVA): IRGBA {
  // Handle overflows for hue
  const h = (hue < 0 ? 6 : 0) + (hue / 60) % 6;
  const c = value * saturation;
  const x = c * (1 - Math.abs((h % 2) - 1));
  const min = value - c;

  return (
    h <= 1 ? { red: c + min, green: x + min, blue: 0 + min, alpha } :
    h <= 2 ? { red: x + min, green: c + min, blue: 0 + min, alpha } :
    h <= 3 ? { red: 0 + min, green: c + min, blue: x + min, alpha } :
    h <= 4 ? { red: 0 + min, green: x + min, blue: c + min, alpha } :
    h <= 5 ? { red: x + min, green: 0 + min, blue: c + min, alpha } :
    /* h<=6 */ { red: c + min, green: 0 + min, blue: x + min, alpha }
  );
}
